import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import telegramService from '../services/telegram.service';
import { TelegramApiError } from '../utils/telegramErrorParser';
import { useAppFeatures } from './AppFeaturesContext';
import { useAuth } from './AuthContext';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';
import type { TelegramInstanceOut } from '../types/telegram.types';

// TelegramContext — список Telegram-каналов организации + выбранный инстанс.
// Паттерн SelfQrContext: грузим listInstances при наличии фичи, выбор персистим.
// Приоритет выбора: предыдущий выбранный (если жив) → сохранённый в localStorage → первый.

const STORAGE_KEY = 'telegram_selected_instance_id';

interface TelegramContextType {
  instances: TelegramInstanceOut[];
  currentInstance: TelegramInstanceOut | null;
  isLoading: boolean;
  error: string | null;
  setCurrentInstance: (instance: TelegramInstanceOut | null) => void;
  refreshInstances: () => Promise<void>;
}

const Ctx = createContext<TelegramContextType | null>(null);

export const TelegramProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const features = useAppFeatures();
  const { isAuthenticated } = useAuth();
  const [instances, setInstances] = useState<TelegramInstanceOut[]>([]);
  const [currentInstance, setCurrentInstanceState] = useState<TelegramInstanceOut | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshInstances = useCallback(async () => {
    if (!isAuthenticated || !features.telegram) {
      setInstances([]);
      setCurrentInstanceState(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const list = await telegramService.listInstances();
      setInstances(list);
      setCurrentInstanceState((prev) => {
        const storedId = Number(safeGetItem(STORAGE_KEY));
        return (
          list.find((i) => i.id === prev?.id) ??
          list.find((i) => i.id === storedId) ??
          list[0] ??
          null
        );
      });
    } catch (e: unknown) {
      // 403 (нет скоупа read) — не ошибка для UI: модуль просто скрыт.
      if (e instanceof TelegramApiError && e.code === 'forbidden') {
        setInstances([]);
        setCurrentInstanceState(null);
      } else {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить Telegram-каналы');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, features.telegram]);

  useEffect(() => {
    void refreshInstances();
  }, [refreshInstances]);

  const setCurrentInstance = useCallback((instance: TelegramInstanceOut | null) => {
    setCurrentInstanceState(instance);
    if (instance) {
      safeSetItem(STORAGE_KEY, String(instance.id));
    } else {
      safeRemoveItem(STORAGE_KEY);
    }
  }, []);

  const value = useMemo<TelegramContextType>(
    () => ({ instances, currentInstance, isLoading, error, setCurrentInstance, refreshInstances }),
    [instances, currentInstance, isLoading, error, setCurrentInstance, refreshInstances],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useTelegram = (): TelegramContextType => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTelegram must be used within TelegramProvider');
  return v;
};
